import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useWallet } from '../components/WalletContext';
import {
  subscribeToSubmissions,
  updateSubmissionStatus,
  deleteSubmission as deleteSubmissionFromDB,
  addFeaturedCollection as addFeaturedToDB,
  getFeaturedCollections,
  addVerifiedCreator as addVerifiedToDB,
  getVerifiedCreators,
  isUsingFirebase
} from '../services/firebase';
import {
  FaShieldAlt,
  FaCrown,
  FaStar,
  FaBullhorn,
  FaRocket,
  FaCheck,
  FaTimes,
  FaExternalLinkAlt,
  FaCopy,
  FaTrash,
  FaSync,
  FaCoins,
  FaWallet,
  FaImage,
  FaClock,
  FaFilter,
  FaSearch,
  FaPlus,
  FaEdit,
  FaSave,
  FaUpload,
  FaEye,
  FaDownload,
  FaLink
} from 'react-icons/fa';
import '../styles/AdminPage.css';

// Admin wallet addresses - only these can access the panel (lowercase for comparison)
const ADMIN_WALLETS = [
  'klv1pwh5qeqh5s582s93sx0d3tfsnjth85jevugtgclwtg7sjzpp9muqw877kl'  // Admin wallet
];

// Platform fee wallet (same as in CreatorPage) - receives all service payments
const PLATFORM_FEE_WALLET = 'klv1pwh5qeqh5s582s93sx0d3tfsnjth85jevugtgclwtg7sjzpp9muqw877kl';

// Service icons mapping
const SERVICE_ICONS = {
  featured_ito: FaStar,
  verified_creator: FaShieldAlt,
  launch_boost: FaBullhorn,
  drops_listing: FaRocket
};

const AdminPage = () => {
  const { isConnected, walletAddress, connectWallet } = useWallet();

  // States
  const [activeTab, setActiveTab] = useState('submissions');
  const [purchases, setPurchases] = useState([]);
  const [featuredCollections, setFeaturedCollections] = useState([]);
  const [verifiedCreators, setVerifiedCreators] = useState([]);
  const [blockchainPurchases, setBlockchainPurchases] = useState([]);
  const [materialSubmissions, setMaterialSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // Manual add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormData, setAddFormData] = useState({
    type: 'featured',
    assetId: '',
    name: '',
    wallet: '',
    expiresAt: ''
  });

  // Check if current wallet is admin
  const isAdmin = isConnected && ADMIN_WALLETS.includes(walletAddress?.toLowerCase());

  // Load data from Firebase (with localStorage fallback)
  useEffect(() => {
    loadLocalData();
    if (isAdmin) {
      fetchBlockchainPurchases();
    }

    // Subscribe to real-time submission updates from Firebase
    const unsubscribe = subscribeToSubmissions((submissions) => {
      setMaterialSubmissions(submissions);
    });

    return () => unsubscribe();
  }, [isAdmin]);

  const loadLocalData = async () => {
    // Load purchases
    const savedPurchases = JSON.parse(localStorage.getItem('rarecanvas_service_purchases') || '[]');
    setPurchases(savedPurchases);

    // Load featured collections (from Firebase or localStorage)
    const savedFeatured = await getFeaturedCollections();
    setFeaturedCollections(savedFeatured);

    // Load verified creators (from Firebase or localStorage)
    const savedVerified = await getVerifiedCreators();
    setVerifiedCreators(savedVerified);
  };

  // Fetch purchases from blockchain (transactions to platform wallet)
  const fetchBlockchainPurchases = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `https://api.mainnet.klever.org/v1.0/transaction/list?receiver=${PLATFORM_FEE_WALLET}&limit=100&type=0`
      );

      const txs = response.data.data?.transactions || [];

      // Parse transactions and extract service purchase data
      const parsedPurchases = txs.map(tx => {
        let serviceData = null;
        try {
          // Try to parse data field for service info
          const dataField = tx.contract?.[0]?.parameter?.data?.[0];
          if (dataField) {
            serviceData = JSON.parse(dataField);
          }
        } catch (e) {
          // Not a service purchase or invalid data
        }

        const amount = (tx.contract?.[0]?.parameter?.amount || 0) / Math.pow(10, 6);

        return {
          txHash: tx.hash,
          sender: tx.sender,
          amount: amount,
          timestamp: tx.timestamp * 1000,
          serviceData: serviceData,
          isServicePurchase: serviceData?.type === 'service_purchase'
        };
      }).filter(tx => tx.isServicePurchase || tx.amount >= 250); // Show service purchases or large transfers

      setBlockchainPurchases(parsedPurchases);
    } catch (error) {
      console.error('Error fetching blockchain purchases:', error);
    }
    setIsLoading(false);
  };

  // Approve a purchase
  const handleApprovePurchase = (purchaseIndex) => {
    const updatedPurchases = [...purchases];
    const purchase = updatedPurchases[purchaseIndex];
    purchase.status = 'approved';
    purchase.approvedAt = Date.now();
    purchase.approvedBy = walletAddress;

    // If it's a featured ITO, add to featured collections
    if (purchase.service === 'featured_ito' && purchase.targetAsset) {
      const newFeatured = {
        assetId: purchase.targetAsset,
        name: purchase.targetName,
        wallet: purchase.wallet,
        addedAt: Date.now(),
        expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days
        service: 'featured_ito',
        txHash: purchase.txHash
      };
      const updatedFeatured = [...featuredCollections, newFeatured];
      setFeaturedCollections(updatedFeatured);
      localStorage.setItem('rarecanvas_featured_collections', JSON.stringify(updatedFeatured));
    }

    // If it's verified creator, add to verified list
    if (purchase.service === 'verified_creator') {
      const newVerified = {
        wallet: purchase.wallet,
        addedAt: Date.now(),
        txHash: purchase.txHash
      };
      const updatedVerified = [...verifiedCreators, newVerified];
      setVerifiedCreators(updatedVerified);
      localStorage.setItem('rarecanvas_verified_creators', JSON.stringify(updatedVerified));
    }

    // If it's launch boost, add to featured with longer duration
    if (purchase.service === 'launch_boost' && purchase.targetAsset) {
      const newFeatured = {
        assetId: purchase.targetAsset,
        name: purchase.targetName,
        wallet: purchase.wallet,
        addedAt: Date.now(),
        expiresAt: Date.now() + (14 * 24 * 60 * 60 * 1000), // 14 days
        service: 'launch_boost',
        txHash: purchase.txHash,
        isBoost: true
      };
      const updatedFeatured = [...featuredCollections, newFeatured];
      setFeaturedCollections(updatedFeatured);
      localStorage.setItem('rarecanvas_featured_collections', JSON.stringify(updatedFeatured));
    }

    setPurchases(updatedPurchases);
    localStorage.setItem('rarecanvas_service_purchases', JSON.stringify(updatedPurchases));
  };

  // Reject a purchase
  const handleRejectPurchase = (purchaseIndex, reason = '') => {
    const updatedPurchases = [...purchases];
    updatedPurchases[purchaseIndex].status = 'rejected';
    updatedPurchases[purchaseIndex].rejectedAt = Date.now();
    updatedPurchases[purchaseIndex].rejectedBy = walletAddress;
    updatedPurchases[purchaseIndex].rejectReason = reason;

    setPurchases(updatedPurchases);
    localStorage.setItem('rarecanvas_service_purchases', JSON.stringify(updatedPurchases));
  };

  // Remove from featured
  const handleRemoveFeatured = (index) => {
    const updatedFeatured = featuredCollections.filter((_, i) => i !== index);
    setFeaturedCollections(updatedFeatured);
    localStorage.setItem('rarecanvas_featured_collections', JSON.stringify(updatedFeatured));
  };

  // Remove verified creator
  const handleRemoveVerified = (index) => {
    const updatedVerified = verifiedCreators.filter((_, i) => i !== index);
    setVerifiedCreators(updatedVerified);
    localStorage.setItem('rarecanvas_verified_creators', JSON.stringify(updatedVerified));
  };

  // Approve a material submission
  const handleApproveSubmission = async (submissionId) => {
    const submission = materialSubmissions.find(sub => sub.id === submissionId || sub.firebaseKey === submissionId);
    if (!submission) return;

    // Update submission status in Firebase
    await updateSubmissionStatus(submissionId, 'approved', {
      approvedAt: Date.now(),
      approvedBy: walletAddress
    });

    // Add to featured collections if launch_boost
    if (submission.service === 'launch_boost' && submission.collectionId) {
      const newFeatured = {
        assetId: submission.collectionId,
        name: submission.materials?.description || submission.collectionId,
        wallet: submission.wallet,
        addedAt: Date.now(),
        expiresAt: Date.now() + (14 * 24 * 60 * 60 * 1000), // 14 days
        service: 'launch_boost',
        txHash: submission.txHash,
        isBoost: true,
        bannerImage: submission.images?.banner?.data,
        logoImage: submission.images?.logo?.data
      };
      await addFeaturedToDB(newFeatured);
      setFeaturedCollections(prev => [...prev, newFeatured]);
    }

    // Add to verified creators if verified_creator
    if (submission.service === 'verified_creator') {
      const newVerified = {
        wallet: submission.wallet,
        addedAt: Date.now(),
        txHash: submission.txHash,
        collection: submission.materials?.collection
      };
      await addVerifiedToDB(newVerified);
      setVerifiedCreators(prev => [...prev, newVerified]);
    }

    setSelectedSubmission(null);
  };

  // Reject a material submission
  const handleRejectSubmission = async (submissionId, reason = '') => {
    await updateSubmissionStatus(submissionId, 'rejected', {
      rejectedAt: Date.now(),
      rejectedBy: walletAddress,
      rejectReason: reason
    });
    setSelectedSubmission(null);
  };

  // Delete a submission
  const handleDeleteSubmission = async (submissionId) => {
    await deleteSubmissionFromDB(submissionId);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Format date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if featured is expired
  const isExpired = (expiresAt) => {
    return Date.now() > expiresAt;
  };

  // Filter purchases
  const filteredPurchases = purchases.filter(p => {
    if (filter !== 'all' && p.status !== filter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        p.wallet?.toLowerCase().includes(search) ||
        p.targetAsset?.toLowerCase().includes(search) ||
        p.serviceName?.toLowerCase().includes(search) ||
        p.txHash?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Manual add featured collection
  const handleManualAdd = async () => {
    if (!addFormData.assetId) return;

    // Fetch collection info
    let collectionName = addFormData.name;
    if (!collectionName) {
      try {
        const response = await axios.get(`https://api.mainnet.klever.org/v1.0/assets/${addFormData.assetId}`);
        collectionName = response.data.data?.asset?.name || addFormData.assetId;
      } catch (e) {
        collectionName = addFormData.assetId;
      }
    }

    if (addFormData.type === 'featured') {
      const newFeatured = {
        assetId: addFormData.assetId,
        name: collectionName,
        wallet: addFormData.wallet || 'manual',
        addedAt: Date.now(),
        expiresAt: addFormData.expiresAt
          ? new Date(addFormData.expiresAt).getTime()
          : Date.now() + (7 * 24 * 60 * 60 * 1000),
        service: 'manual',
        isManual: true
      };
      const updated = [...featuredCollections, newFeatured];
      setFeaturedCollections(updated);
      localStorage.setItem('rarecanvas_featured_collections', JSON.stringify(updated));
    } else if (addFormData.type === 'verified') {
      const newVerified = {
        wallet: addFormData.wallet,
        addedAt: Date.now(),
        isManual: true
      };
      const updated = [...verifiedCreators, newVerified];
      setVerifiedCreators(updated);
      localStorage.setItem('rarecanvas_verified_creators', JSON.stringify(updated));
    }

    setShowAddForm(false);
    setAddFormData({ type: 'featured', assetId: '', name: '', wallet: '', expiresAt: '' });
  };

  // Not connected
  if (!isConnected) {
    return (
      <div className="admin-page">
        <div className="admin-connect-prompt">
          <FaShieldAlt className="admin-icon" />
          <h2>Admin Panel</h2>
          <p>Connect your admin wallet to access the panel.</p>
          <button className="admin-connect-btn" onClick={connectWallet}>
            <FaWallet /> Connect Wallet
          </button>
        </div>
      </div>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-denied">
          <FaTimes className="admin-icon denied" />
          <h2>Access Denied</h2>
          <p>Your wallet is not authorized to access the admin panel.</p>
          <p className="wallet-display">{walletAddress}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <h1><FaCrown /> Admin Panel</h1>
          <p>Manage service purchases, featured collections, and verified creators</p>
        </div>
        <button className="refresh-btn" onClick={() => { loadLocalData(); fetchBlockchainPurchases(); }}>
          <FaSync className={isLoading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Quick Stats */}
      <div className="admin-stats">
        <div className="admin-stat">
          <FaClock />
          <div className="stat-info">
            <span className="stat-value">{purchases.filter(p => p.status === 'pending').length}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <div className="admin-stat">
          <FaStar />
          <div className="stat-info">
            <span className="stat-value">{featuredCollections.filter(f => !isExpired(f.expiresAt)).length}</span>
            <span className="stat-label">Active Featured</span>
          </div>
        </div>
        <div className="admin-stat">
          <FaShieldAlt />
          <div className="stat-info">
            <span className="stat-value">{verifiedCreators.length}</span>
            <span className="stat-label">Verified Creators</span>
          </div>
        </div>
        <div className="admin-stat">
          <FaCoins />
          <div className="stat-info">
            <span className="stat-value">{purchases.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}</span>
            <span className="stat-label">Total KLV</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          <FaUpload /> Submissions
          {materialSubmissions.filter(s => s.status === 'pending').length > 0 && (
            <span className="tab-badge">{materialSubmissions.filter(s => s.status === 'pending').length}</span>
          )}
        </button>
        <button
          className={`admin-tab ${activeTab === 'purchases' ? 'active' : ''}`}
          onClick={() => setActiveTab('purchases')}
        >
          <FaCoins /> Purchases
          {purchases.filter(p => p.status === 'pending').length > 0 && (
            <span className="tab-badge">{purchases.filter(p => p.status === 'pending').length}</span>
          )}
        </button>
        <button
          className={`admin-tab ${activeTab === 'featured' ? 'active' : ''}`}
          onClick={() => setActiveTab('featured')}
        >
          <FaStar /> Featured
        </button>
        <button
          className={`admin-tab ${activeTab === 'verified' ? 'active' : ''}`}
          onClick={() => setActiveTab('verified')}
        >
          <FaShieldAlt /> Verified
        </button>
        <button
          className={`admin-tab ${activeTab === 'blockchain' ? 'active' : ''}`}
          onClick={() => setActiveTab('blockchain')}
        >
          <FaWallet /> Blockchain
        </button>
      </div>

      {/* Content */}
      <div className="admin-content">
        {/* Material Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="submissions-section">
            <div className="section-toolbar">
              <h3><FaUpload /> Material Submissions</h3>
              <a href="/submit-materials" target="_blank" className="add-btn">
                <FaExternalLinkAlt /> Submission Page
              </a>
            </div>

            {materialSubmissions.length > 0 ? (
              <div className="submissions-list">
                {materialSubmissions.map((submission) => {
                  const Icon = SERVICE_ICONS[submission.service] || FaCrown;
                  return (
                    <div key={submission.id} className={`submission-item ${submission.status}`}>
                      <div className="submission-header">
                        <div className="submission-icon">
                          <Icon />
                        </div>
                        <div className="submission-title">
                          <h4>{submission.serviceName}</h4>
                          <span className={`status-badge ${submission.status}`}>{submission.status}</span>
                        </div>
                        <span className="submission-date">{formatDate(submission.submittedAt)}</span>
                      </div>

                      <div className="submission-details">
                        <div className="detail-row">
                          <span className="label">Wallet:</span>
                          <span className="value">
                            {submission.wallet?.slice(0, 16)}...{submission.wallet?.slice(-8)}
                            <button className="copy-btn" onClick={() => copyToClipboard(submission.wallet)}>
                              <FaCopy />
                            </button>
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Tx Hash:</span>
                          <span className="value">
                            <a href={`https://kleverscan.org/transaction/${submission.txHash}`} target="_blank" rel="noopener noreferrer">
                              {submission.txHash?.slice(0, 16)}... <FaExternalLinkAlt />
                            </a>
                          </span>
                        </div>
                        {submission.collectionId && (
                          <div className="detail-row">
                            <span className="label">Collection:</span>
                            <span className="value">{submission.collectionId}</span>
                          </div>
                        )}
                      </div>

                      {/* Show uploaded images */}
                      {submission.images && Object.keys(submission.images).length > 0 && (
                        <div className="submission-images">
                          <span className="images-label">Uploaded Images:</span>
                          <div className="images-grid">
                            {Object.entries(submission.images).map(([key, img]) => (
                              <div key={key} className="image-preview">
                                <img src={img.data} alt={key} />
                                <span className="image-label">{key}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show text materials */}
                      {submission.materials && Object.keys(submission.materials).length > 0 && (
                        <div className="submission-materials">
                          <span className="materials-label">Materials:</span>
                          <div className="materials-list">
                            {Object.entries(submission.materials).map(([key, value]) => (
                              <div key={key} className="material-item">
                                <span className="material-key">{key}:</span>
                                {value?.startsWith('http') ? (
                                  <a href={value} target="_blank" rel="noopener noreferrer" className="material-link">
                                    {value} <FaLink />
                                  </a>
                                ) : (
                                  <span className="material-value">{value}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {submission.status === 'pending' && (
                        <div className="submission-actions">
                          <button
                            className="approve-btn"
                            onClick={() => handleApproveSubmission(submission.id)}
                          >
                            <FaCheck /> Approve & Activate
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => handleRejectSubmission(submission.id, 'Rejected by admin')}
                          >
                            <FaTimes /> Reject
                          </button>
                        </div>
                      )}

                      {submission.status !== 'pending' && (
                        <div className="submission-actions">
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteSubmission(submission.id)}
                          >
                            <FaTrash /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <FaUpload />
                <p>No submissions yet</p>
                <span>Projects submit materials at <a href="/submit-materials">/submit-materials</a></span>
              </div>
            )}
          </div>
        )}

        {/* Service Purchases Tab */}
        {activeTab === 'purchases' && (
          <div className="purchases-section">
            <div className="section-toolbar">
              <div className="filter-group">
                <FaFilter />
                <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="all">All Purchases</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="search-group">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search by wallet, asset, service..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {filteredPurchases.length > 0 ? (
              <div className="purchases-list">
                {filteredPurchases.map((purchase, index) => {
                  const Icon = SERVICE_ICONS[purchase.service] || FaCrown;
                  return (
                    <div key={index} className={`purchase-item ${purchase.status}`}>
                      <div className="purchase-icon">
                        <Icon />
                      </div>
                      <div className="purchase-info">
                        <div className="purchase-header">
                          <h4>{purchase.serviceName}</h4>
                          <span className={`status-badge ${purchase.status}`}>
                            {purchase.status}
                          </span>
                        </div>
                        <div className="purchase-details">
                          <div className="detail-row">
                            <span className="label">Wallet:</span>
                            <span className="value">
                              {purchase.wallet?.slice(0, 12)}...{purchase.wallet?.slice(-8)}
                              <button className="copy-btn" onClick={() => copyToClipboard(purchase.wallet)}>
                                <FaCopy />
                              </button>
                            </span>
                          </div>
                          {purchase.targetAsset && (
                            <div className="detail-row">
                              <span className="label">Collection:</span>
                              <span className="value">{purchase.targetName || purchase.targetAsset}</span>
                            </div>
                          )}
                          <div className="detail-row">
                            <span className="label">Amount:</span>
                            <span className="value">{purchase.amount?.toLocaleString()} KLV</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Date:</span>
                            <span className="value">{formatDate(purchase.timestamp)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Tx Hash:</span>
                            <span className="value">
                              <a
                                href={`https://kleverscan.org/transaction/${purchase.txHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {purchase.txHash?.slice(0, 12)}... <FaExternalLinkAlt />
                              </a>
                            </span>
                          </div>
                        </div>
                      </div>
                      {purchase.status === 'pending' && (
                        <div className="purchase-actions">
                          <button
                            className="approve-btn"
                            onClick={() => handleApprovePurchase(purchases.indexOf(purchase))}
                          >
                            <FaCheck /> Approve
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => handleRejectPurchase(purchases.indexOf(purchase))}
                          >
                            <FaTimes /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <FaCoins />
                <p>No purchases found</p>
              </div>
            )}
          </div>
        )}

        {/* Featured Collections Tab */}
        {activeTab === 'featured' && (
          <div className="featured-section">
            <div className="section-toolbar">
              <h3><FaStar /> Active Featured Collections</h3>
              <button className="add-btn" onClick={() => setShowAddForm(true)}>
                <FaPlus /> Add Manual
              </button>
            </div>

            {showAddForm && (
              <div className="add-form">
                <h4>Add Featured Collection</h4>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Asset ID (e.g., COLLECTION-1A2B)"
                    value={addFormData.assetId}
                    onChange={(e) => setAddFormData({ ...addFormData, assetId: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Name (optional)"
                    value={addFormData.name}
                    onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Creator Wallet (optional)"
                    value={addFormData.wallet}
                    onChange={(e) => setAddFormData({ ...addFormData, wallet: e.target.value })}
                  />
                  <input
                    type="datetime-local"
                    value={addFormData.expiresAt}
                    onChange={(e) => setAddFormData({ ...addFormData, expiresAt: e.target.value })}
                  />
                </div>
                <div className="form-actions">
                  <button className="cancel-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
                  <button className="save-btn" onClick={handleManualAdd}>
                    <FaSave /> Save
                  </button>
                </div>
              </div>
            )}

            {featuredCollections.length > 0 ? (
              <div className="featured-list">
                {featuredCollections.map((featured, index) => (
                  <div
                    key={index}
                    className={`featured-item ${isExpired(featured.expiresAt) ? 'expired' : 'active'} ${featured.isBoost ? 'boost' : ''}`}
                  >
                    <div className="featured-icon">
                      {featured.isBoost ? <FaBullhorn /> : <FaStar />}
                    </div>
                    <div className="featured-info">
                      <h4>{featured.name || featured.assetId}</h4>
                      <p className="asset-id">{featured.assetId}</p>
                      <div className="featured-meta">
                        <span>Added: {formatDate(featured.addedAt)}</span>
                        <span className={isExpired(featured.expiresAt) ? 'expired-text' : ''}>
                          Expires: {formatDate(featured.expiresAt)}
                        </span>
                        <span>Service: {featured.service}</span>
                      </div>
                    </div>
                    <div className="featured-actions">
                      <a
                        href={`/collections/${featured.assetId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-btn"
                      >
                        <FaExternalLinkAlt />
                      </a>
                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveFeatured(index)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FaStar />
                <p>No featured collections</p>
              </div>
            )}
          </div>
        )}

        {/* Verified Creators Tab */}
        {activeTab === 'verified' && (
          <div className="verified-section">
            <div className="section-toolbar">
              <h3><FaShieldAlt /> Verified Creators</h3>
              <button className="add-btn" onClick={() => {
                setAddFormData({ ...addFormData, type: 'verified' });
                setShowAddForm(true);
              }}>
                <FaPlus /> Add Verified
              </button>
            </div>

            {showAddForm && addFormData.type === 'verified' && (
              <div className="add-form">
                <h4>Add Verified Creator</h4>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Wallet Address"
                    value={addFormData.wallet}
                    onChange={(e) => setAddFormData({ ...addFormData, wallet: e.target.value })}
                  />
                </div>
                <div className="form-actions">
                  <button className="cancel-btn" onClick={() => setShowAddForm(false)}>Cancel</button>
                  <button className="save-btn" onClick={handleManualAdd}>
                    <FaSave /> Save
                  </button>
                </div>
              </div>
            )}

            {verifiedCreators.length > 0 ? (
              <div className="verified-list">
                {verifiedCreators.map((creator, index) => (
                  <div key={index} className="verified-item">
                    <div className="verified-icon">
                      <FaShieldAlt />
                    </div>
                    <div className="verified-info">
                      <p className="wallet-address">
                        {creator.wallet}
                        <button className="copy-btn" onClick={() => copyToClipboard(creator.wallet)}>
                          <FaCopy />
                        </button>
                      </p>
                      <span className="verified-date">Verified: {formatDate(creator.addedAt)}</span>
                    </div>
                    <div className="verified-actions">
                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveVerified(index)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FaShieldAlt />
                <p>No verified creators</p>
              </div>
            )}
          </div>
        )}

        {/* Blockchain Transactions Tab */}
        {activeTab === 'blockchain' && (
          <div className="blockchain-section">
            <div className="section-toolbar">
              <h3><FaWallet /> Recent Transactions to Platform Wallet</h3>
              <button className="refresh-btn" onClick={fetchBlockchainPurchases}>
                <FaSync className={isLoading ? 'spin' : ''} /> Refresh
              </button>
            </div>

            {isLoading ? (
              <div className="loading-state">
                <FaSync className="spin" />
                <p>Loading blockchain data...</p>
              </div>
            ) : blockchainPurchases.length > 0 ? (
              <div className="blockchain-list">
                {blockchainPurchases.map((tx, index) => (
                  <div key={index} className={`blockchain-item ${tx.isServicePurchase ? 'service' : ''}`}>
                    <div className="tx-info">
                      <div className="tx-header">
                        <span className="tx-amount">{tx.amount.toLocaleString()} KLV</span>
                        {tx.isServicePurchase && (
                          <span className="service-badge">{tx.serviceData?.service}</span>
                        )}
                      </div>
                      <div className="tx-details">
                        <span>From: {tx.sender?.slice(0, 12)}...{tx.sender?.slice(-8)}</span>
                        <span>{formatDate(tx.timestamp)}</span>
                      </div>
                      {tx.serviceData?.target && (
                        <div className="tx-target">
                          Target: {tx.serviceData.target}
                        </div>
                      )}
                    </div>
                    <a
                      href={`https://kleverscan.org/transaction/${tx.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tx-link"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FaWallet />
                <p>No transactions found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
